import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Radio, Lock, MessageSquare, Send, Heart } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { EqBars } from "../components/Brand";
import { LivePlayer } from "../components/LivePlayer";
import { TipPanel } from "../components/TipPanel";
import RecordingCard from "../components/RecordingCard";
import { Spinner, formatViews } from "../components/states";
import { toast } from "sonner";

export default function Live() {
  const { user } = useAuth();
  const [live, setLive] = useState(null);
  const [recs, setRecs] = useState([]);
  const [comments, setComments] = useState([]);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const isMember = user && (user.is_member || ["DJ", "ADMIN", "SUPER_ADMIN"].includes(user.role));

  useEffect(() => {
    const sid = sessionStorage.getItem("rav3_sid") || (() => { const s = Math.random().toString(36).slice(2, 14); sessionStorage.setItem("rav3_sid", s); return s; })();
    const beat = () => api.post("/live/heartbeat", { sid }).catch(() => {});
    beat();
    const t = setInterval(async () => { await beat(); try { const l = await api.get("/live"); setLive(l.data && l.data.id ? l.data : null); } catch {} }, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [l, r] = await Promise.all([api.get("/live"), api.get("/recordings?sort=popular")]);
        setLive(l.data && l.data.id ? l.data : null);
        setRecs(r.data);
        const featured = r.data[0];
        if (featured) {
          const c = await api.get(`/recordings/${featured.id}/comments`);
          setComments(c.data);
        }
      } catch { /* handled by empty UI */ }
      setLoading(false);
    })();
  }, []);

  const sendComment = async () => {
    if (!msg.trim()) return;
    if (!user) { toast.error("Sign in to comment"); return; }
    if (!isMember) { toast.error("Members only — become a member to comment"); return; }
    try {
      const { data } = await api.post(`/recordings/${recs[0].id}/comments`, { body: msg });
      setComments([data, ...comments]); setMsg("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <Spinner label="Tuning in" />;

  return (
    <div className="mx-auto max-w-[1600px] px-4 sm:px-6 py-6">
      <div className="grid lg:grid-cols-[1fr_380px] gap-6">
        {/* Player */}
        <div>
          <div className="flex items-center gap-4 mb-3">
            {live ? (
              <span data-testid="live-now-badge" className="live-pulse inline-flex items-center gap-2 rounded-md border border-[#10B981]/50 bg-[#10B981]/10 px-3 py-1.5">
                <Radio size={14} className="text-[#10B981]" /><span className="mono-label text-[#10B981]">LIVE NOW</span>
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 rounded-md border border-[#27272A] px-3 py-1.5"><span className="mono-label text-[#71717A]">OFFLINE</span></span>
            )}
            {live && <span className="mono-label text-[#A1A1AA]">{formatViews(live.viewers)} viewers</span>}
            {live && <EqBars />}
          </div>

          <LivePlayer live={live} poster={live?.cover_image || recs[0]?.cover_image} />
          {live && <p className="font-display font-bold text-xl mt-4">{live.title} <span className="mono-label text-[#A1A1AA] font-normal">· {live.dj_name}</span></p>}
          {live && <TipPanel live={live} user={user} />}

          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl font-bold uppercase tracking-wide">Archive</h2>
              <Link to="/library" className="mono-label text-[#E8552A] hover:underline">VIEW ALL →</Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {recs.slice(0, 9).map((r, i) => <RecordingCard key={r.id} rec={r} index={i} />)}
            </div>
          </div>
        </div>

        {/* Side panels */}
        <aside className="space-y-5">
          <div data-testid="track-id-panel" className="card-copper rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2"><EqBars playing={!!live} /><span className="font-display font-bold uppercase tracking-wide">Track ID</span></div>
              <span className="mono-label text-[#71717A]">PRIME & ABOVE</span>
            </div>
            {recs[0]?.tracklist?.length ? (
              <div className="space-y-3">
                {(recs[0].tracklist || []).map((t, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="mono-label text-[#E8552A] pt-0.5">{t.time}</span>
                    <div><p className="text-sm text-[#F4F4F5]">{t.title}</p><p className="mono-label text-[#71717A]">{t.artist} · {t.label}</p></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="relative">
                <div className="space-y-3 blur-sm select-none pointer-events-none">
                  {[0, 1, 2].map((i) => (<div key={i} className="flex gap-3"><span className="mono-label text-[#E8552A]">00:0{i}</span><div className="h-3 bg-[#27272A] rounded w-40" /></div>))}
                </div>
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                  <Lock className="text-[#E8552A]" size={22} />
                  <p className="text-sm text-[#A1A1AA] mt-2">Track IDs appear here live</p>
                  <Link to="/membership" data-testid="unlock-trackid-btn" className="mono-label text-[#E8552A] mt-1 hover:underline">UNLOCK NOW →</Link>
                </div>
              </div>
            )}
          </div>

          <div data-testid="live-chat-panel" className="card-copper rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4"><MessageSquare size={16} className="text-[#E8552A]" /><span className="font-display font-bold uppercase tracking-wide">Live Comments</span><span className="mono-label text-[#71717A] ml-auto">{comments.length}</span></div>
            <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
              {comments.length === 0 && <p className="text-sm text-[#71717A]">Be the first to comment.</p>}
              {comments.map((c) => (
                <div key={c.id}>
                  <span className="mono-label text-[#E8552A]">{c.handle}</span>
                  <p className="text-sm text-[#D4D4D8]">{c.body}</p>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-4">
              <input data-testid="comment-input" value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendComment()}
                placeholder={isMember ? "Add a comment…" : "Members only"} className="flex-1 bg-[#0A0A0C] border border-[#27272A] rounded-lg px-3 py-2 text-sm focus:border-[#E8552A] outline-none" />
              <button data-testid="send-comment-btn" onClick={sendComment} className="copper-btn rounded-lg p-2"><Send size={16} /></button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
